import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import {useForm, FormProvider} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import 'src/validators/israeli-id-validator'
import FormTextField from 'src/components/ui/TextField'

type QuestionProps = {
  variant: 'question'
  onYes: () => void
  onProceed: () => void
}

type LoginProps = {
  variant: 'login'
  onSubmit: (institutionCode: string, contactId: string, otp?: string) => void
  isLoading: boolean
  errorMessage: string | null
  otpSent: boolean
}

type Props = QuestionProps | LoginProps

const loginSchema = Yup.object({
  institutionCode: Yup.string().required('קוד מוסד הוא שדה חובה'),
  contactId: Yup.string()
    .required('תעודת זהות היא שדה חובה')
    .israeliIdentity('מספר תעודת זהות לא תקין'),
  otp: Yup.string().optional()
})

const CardWrapper = ({children}: {children: React.ReactNode}) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    dir="rtl"
    mt={6}
  >
    <Paper
      elevation={3}
      sx={{borderRadius: 2, p: 6, width: 500, textAlign: 'center'}}
    >
      {children}
    </Paper>
  </Box>
)

const QuestionContent = ({
  onYes,
  onProceed
}: Omit<QuestionProps, 'variant'>) => (
  <Stack spacing={4} alignItems="center">
    <Typography variant="h6">
      האם אתה רשום בקרן עולם התורה ומעונין ליבא את נתוני המוסד משם?
    </Typography>
    <Stack direction="row" gap={2}>
      <Button variant="contained" size="medium" onClick={onYes}>
        כן
      </Button>
      <Button variant="outlined" size="medium" onClick={onProceed}>
        לא
      </Button>
    </Stack>
  </Stack>
)

const LoginContent = ({
  onSubmit,
  isLoading,
  errorMessage,
  otpSent
}: Omit<LoginProps, 'variant'>) => {
  const methods = useForm({
    resolver: yupResolver(loginSchema),
    mode: 'onChange'
  })

  const handleSubmit = (data: {
    institutionCode: string
    contactId?: string
    otp?: string
  }) => {
    if (otpSent && !data.otp?.trim()) {
      methods.setError('otp', {message: 'סיסמה חד פעמית היא שדה חובה'})
      return
    }
    onSubmit(data.institutionCode, data.contactId ?? '', data.otp)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h6">
            הכנס את פרטי המוסד ב'קרן עולם התורה'
          </Typography>
          <FormTextField
            name="institutionCode"
            label="קוד מוסד"
            disabled={otpSent}
          />
          <FormTextField
            name="contactId"
            label="מספר תעודת זהות"
            disabled={otpSent}
          />
          {otpSent && (
            <FormTextField name="otp" label="סיסמה חד פעמית" type="password" />
          )}
          {errorMessage && (
            <Alert severity="error" sx={{width: '100%'}}>
              {errorMessage}
            </Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            fullWidth
          >
            {otpSent ? 'יבא נתונים' : 'קבלת סיסמה חד פעמית למייל'}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  )
}

const KOHCard = (props: Props) => (
  <CardWrapper>
    {props.variant === 'question' ? (
      <QuestionContent onYes={props.onYes} onProceed={props.onProceed} />
    ) : (
      <LoginContent
        onSubmit={props.onSubmit}
        isLoading={props.isLoading}
        errorMessage={props.errorMessage}
        otpSent={props.otpSent}
      />
    )}
  </CardWrapper>
)

export default KOHCard
